<script lang="ts">
  import { t } from '$i18n/index';
  import type { MessageKey } from '$i18n/index';
  import { DEFAULT_TRIGGER_TAGS } from '$domain/comeback';
  import { focusOnMount } from '$ui/focus';

  interface Props {
    onpick: (tag: string) => Promise<void>;
    onback: () => void;
  }

  const { onpick, onback }: Props = $props();

  /**
   * Tap 2 — and the tap that writes the Catch to the log.
   *
   * The five defaults are gradually replaced by the user's own most common and
   * custom triggers, so the screen increasingly sounds like their own mind
   * (spec §6.2). That ordering is derived from Catch history by a reducer and
   * never written back; I3 supplies it. Here the defaults stand in.
   *
   * The tags are descriptions, not diagnoses. "Avoiding something" is the
   * furthest this app goes toward interpretation, and even that is the user's
   * own word for it, chosen by them, not inferred by us.
   */
  let busy = $state(false);
  let failed = $state(false);

  async function pick(tag: string) {
    if (busy) return;
    // A second tap while the first is still writing would log two Catches for
    // one noticing. Guarding here rather than disabling the buttons keeps them
    // from flickering grey under the thumb.
    busy = true;
    failed = false;
    try {
      await onpick(tag);
    } catch {
      // Storage can fail — private mode, no quota, an evicted database. The
      // guard must come back off, or the picker is inert and the only way out
      // is Back, which loses the Catch the user came here to log.
      busy = false;
      failed = true;
    }
  }
</script>

<main>
  <h1 tabindex="-1" use:focusOnMount>{t('tag.question')}</h1>

  <ul>
    {#each DEFAULT_TRIGGER_TAGS as tag (tag)}
      <li>
        <button type="button" onclick={() => pick(tag)}>
          {t(`tag.${tag}` as MessageKey)}
        </button>
      </li>
    {/each}
  </ul>

  <p class="failed" role="alert">{failed ? t('error.notSaved') : ''}</p>

  <button type="button" class="back" onclick={onback}>{t('tag.back')}</button>
</main>

<style>
  /* Bottom-weighted for thumb reach, same reason as the Catch button. */
  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: var(--space-5);
    padding: var(--space-6) var(--space-5) var(--space-7);
  }

  h1 {
    font-size: var(--step-1);
    font-weight: 500;
    color: var(--c-ink-muted);
    text-align: center;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  li button {
    width: 100%;
    padding: var(--space-4);
    border-radius: var(--radius);
    border: 1px solid var(--c-line);
    background: var(--c-surface);
    color: var(--c-ink);
    font-size: var(--step-1);
    text-align: left;
  }

  li button:active {
    background: var(--c-surface-raised);
  }

  /* Reserves its line so nothing shifts under a thumb when it appears. */
  .failed {
    min-height: 1.5em;
    text-align: center;
    color: var(--c-ink-muted);
    font-size: var(--step--1);
  }

  .back {
    align-self: center;
    color: var(--c-ink-faint);
    font-size: var(--step--1);
    padding: var(--space-2) var(--space-4);
  }
</style>
